import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { getProducts } from '../api';
import { useCart } from '../context/CartContext';
import { flyToCart, showCartToast } from '../context/CartUiContext';
import { useFavorites } from '../hooks/useFavorites';
import { getApiOrigin } from '../utils/apiBase';

const getProductImageUrl = (product) => {
  if (!product?.image) return `https://picsum.photos/200/200?random=${product?.id || 1}`;
  if (String(product.image).startsWith('http')) return product.image;
  const base = getApiOrigin();
  return base + (String(product.image).startsWith('/') ? '' : '/') + product.image;
};

export default function FavoritesDrawer({ open, onClose }) {
  const { favorites, favoritesCount, removeFromFavorites } = useFavorites();
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingId, setAddingId] = useState(null);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    const load = async () => {
      if (!favorites.length) {
        setProducts([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const ids = favorites.map((id) => String(id)).filter(Boolean).join(',');
        const { data } = await getProducts({ ids, lite: true });
        if (!cancelled) setProducts(data || []);
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, favorites]);

  const handleAddToCart = async (product, fromEl) => {
    if ((product.stock ?? 0) <= 0) return;
    setAddingId(product.id);
    try {
      await addToCart(product, 1);
      flyToCart({
        imageSrc: getProductImageUrl(product),
        fromEl: fromEl || null,
      });
    } catch {
      showCartToast('No se pudo agregar al carrito');
    } finally {
      setAddingId(null);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="np-fav-drawer" role="presentation">
      <button
        type="button"
        className="np-fav-drawer__backdrop"
        aria-label="Cerrar favoritos"
        onClick={onClose}
      />
      <aside
        className="np-fav-drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Mis favoritos"
      >
        <header className="np-fav-drawer__header">
          <div>
            <h2 className="np-fav-drawer__title">
              <i className="bi bi-heart-fill" aria-hidden />
              Favoritos
            </h2>
            <p className="np-fav-drawer__subtitle">
              {favoritesCount > 0
                ? `${favoritesCount} producto${favoritesCount !== 1 ? 's' : ''} guardado${favoritesCount !== 1 ? 's' : ''}`
                : 'Guardá productos con el corazón'}
            </p>
          </div>
          <button type="button" className="np-fav-drawer__close" onClick={onClose} aria-label="Cerrar">
            <i className="bi bi-x-lg" />
          </button>
        </header>

        <div className="np-fav-drawer__body">
          {loading ? (
            <div className="np-fav-drawer__loading">
              <div className="spinner-border spinner-border-sm text-primary" role="status" aria-label="Espere" />
            </div>
          ) : favoritesCount === 0 ? (
            <div className="np-fav-drawer__empty">
              <i className="bi bi-heart" aria-hidden />
              <p>Todavía no agregaste favoritos.</p>
              <Link to="/catalogo" className="np-btn-dark" onClick={onClose}>
                Explorar catálogo
              </Link>
            </div>
          ) : products.length === 0 ? (
            <div className="np-fav-drawer__empty">
              <p>No se pudieron cargar los productos.</p>
              <button type="button" className="np-btn-dark" onClick={onClose}>
                Cerrar
              </button>
            </div>
          ) : (
            <ul className="np-fav-drawer__list">
              {products.map((product) => {
                const outOfStock = (product.stock ?? 0) <= 0;
                const isAdding = addingId === product.id;
                return (
                  <li key={product.id} className="np-fav-drawer__item">
                    <Link
                      to={`/producto/${product.id}`}
                      className="np-fav-drawer__thumb"
                      onClick={onClose}
                    >
                      <img
                        src={getProductImageUrl(product)}
                        alt=""
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = `https://picsum.photos/200/200?random=${product.id}`;
                        }}
                      />
                    </Link>
                    <div className="np-fav-drawer__info">
                      <Link
                        to={`/producto/${product.id}`}
                        className="np-fav-drawer__name"
                        onClick={onClose}
                      >
                        {product.name}
                      </Link>
                      <span className="np-fav-drawer__price">
                        ${Number(product.price || 0).toFixed(2)}
                      </span>
                      {outOfStock && <span className="np-fav-drawer__stock">Sin stock</span>}
                    </div>
                    <div className="np-fav-drawer__actions">
                      <button
                        type="button"
                        className="np-fav-drawer__add"
                        disabled={outOfStock || isAdding}
                        aria-label={`Agregar ${product.name} al carrito`}
                        onClick={(e) => handleAddToCart(product, e.currentTarget)}
                      >
                        {isAdding ? (
                          <span className="spinner-border spinner-border-sm" role="status" />
                        ) : (
                          <i className="bi bi-bag-plus" />
                        )}
                      </button>
                      <button
                        type="button"
                        className="np-fav-drawer__remove"
                        aria-label={`Quitar ${product.name} de favoritos`}
                        onClick={(e) => removeFromFavorites(product.id, e.currentTarget)}
                      >
                        <i className="bi bi-heart-fill" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {favoritesCount > 0 && (
          <footer className="np-fav-drawer__footer">
            <Link to="/carrito" className="np-btn-dark np-fav-drawer__footer-btn" onClick={onClose}>
              <i className="bi bi-bag" />
              Ir al carrito
            </Link>
            <Link to="/catalogo" className="np-fav-drawer__link-catalog" onClick={onClose}>
              Ver catálogo completo
            </Link>
          </footer>
        )}
      </aside>
    </div>,
    document.body,
  );
}
